import { useCallback, useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AdminButton, AdminCard, AdminField, adminInput } from "@/components/admin/ui";

const GROUPS = [
  {
    title: "Homepage",
    fields: [
      ["hero_title", "Titolo hero", "textarea"],
      ["hero_subtitle", "Sottotitolo hero", "text"],
      ["hero_eyebrow", "Sopratitolo hero", "text"],
      ["story_title", "Titolo racconto", "text"],
      ["story_text", "Testo racconto", "textarea"],
      ["project_title", "Titolo progetto", "text"],
      ["project_text", "Testo progetto", "textarea"],
      ["product_title", "Titolo materie prime", "text"],
      ["product_text", "Testo materie prime", "textarea"],
    ],
  },
  {
    title: "Orto dell'Osteria (progetto futuro)",
    fields: [
      ["orto_label", "Etichetta", "text"],
      ["orto_enabled", "Mostra la sezione in homepage", "boolean"],
      ["orto_name", "Nome del progetto", "text"],
      ["orto_title", "Titolo", "text"],
      ["orto_text", "Testo", "textarea"],
    ],
  },
  {
    title: "Albergo diffuso (visione futura)",
    fields: [
      ["albergo_label", "Etichetta", "text"],
      ["albergo_enabled", "Mostra la sezione in homepage", "boolean"],
      ["albergo_title", "Titolo", "text"],
      ["albergo_text", "Testo", "textarea"],
      ["albergo_note", "Nota progetto futuro", "text"],
    ],
  },
  {
    title: "Pagina Il progetto",
    fields: [
      ["progetto_intro", "Introduzione", "textarea"],
      ["progetto_borgo_text", "Il punto di partenza", "textarea"],
      ["progetto_closing", "Chiusura", "textarea"],
    ],
  },
  {
    title: "Menu e allergeni",
    fields: [
      ["menu_intro", "Introduzione pagina menu", "textarea"],
      ["allergen_legend", "Legenda allergeni (uno per riga)", "list"],
      ["allergen_note", "Nota allergeni", "textarea"],
    ],
  },
  {
    title: "Il borgo",
    fields: [
      ["borgo_title", "Titolo", "text"],
      ["borgo_text", "Testo", "textarea"],
    ],
  },
  {
    title: "Contatti e orari",
    fields: [
      ["address", "Indirizzo", "text"],
      ["address_line2", "Località", "text"],
      ["phone", "Telefono (visibile)", "text"],
      ["phone_link", "Telefono (per chiamata)", "text"],
      ["email", "Email pubblica", "text"],
      ["instagram", "Instagram", "text"],
      ["maps_url", "Link mappa", "text"],
      ["hours_note", "Nota orari", "textarea"],
    ],
  },
  {
    title: "Immagini delle sezioni (percorso o URL)",
    fields: [
      ["hero_image", "Hero home", "text"],
      ["story_image", "Racconto", "text"],
      ["experience_image_1", "Esperienza 1", "text"],
      ["experience_image_2", "Esperienza 2", "text"],
      ["experience_image_3", "Esperienza 3", "text"],
      ["cuisine_image", "Cucina home", "text"],
      ["project_image", "Progetto", "text"],
      ["product_image", "Materie prime", "text"],
      ["farm_image", "Orto dell'Osteria (non usata)", "text"],
      ["territory_image_1", "Territorio 1", "text"],
      ["territory_image_2", "Territorio 2", "text"],
      ["borgo_header_image", "Header Il borgo", "text"],
      ["borgo_image_1", "Il borgo 1", "text"],
      ["borgo_image_2", "Il borgo 2", "text"],
      ["borgo_image_3", "Il borgo 3", "text"],
      ["cucina_header_image", "Header La cucina", "text"],
      ["cucina_image_1", "La cucina 1", "text"],
      ["menu_header_image", "Header Menu", "text"],
      ["contact_header_image", "Header Contatti", "text"],
      ["prenota_image", "Pagina Prenota", "text"],
    ],
  },
  {
    title: "Stampa e social",
    fields: [
      ["press_quote", "Citazione", "textarea"],
      ["press_source", "Fonte", "text"],
      ["press_url", "Link articolo", "text"],
      ["facebook", "Facebook", "text"],
      ["tagline", "Tagline", "text"],
    ],
  },
  {
    title: "SEO",
    fields: [
      ["seo_home_title", "Titolo homepage", "text"],
      ["seo_home_description", "Descrizione homepage", "textarea"],
    ],
  },
  {
    title: "Prenotazioni online",
    fields: [
      ["max_advance_days", "Giorni massimi di anticipo", "number"],
      ["min_lead_minutes", "Minuti minimi prima del servizio", "number"],
      ["online_booking_enabled", "Prenotazioni online attive", "boolean"],
    ],
  },
];

export default function AdminContent() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setValues(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const payload = { ...values };
      payload.max_advance_days = Number(payload.max_advance_days) || 120;
      payload.min_lead_minutes = Number(payload.min_lead_minutes) || 0;
      payload.online_booking_enabled = !!payload.online_booking_enabled;
      payload.orto_enabled = !!payload.orto_enabled;
      payload.albergo_enabled = !!payload.albergo_enabled;
      const { data } = await api.put("/admin/settings", { values: payload });
      setValues(data);
      setSaved(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-foreground/60">
        <Loader2 size={15} className="animate-spin" /> Carichiamo…
      </p>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-content">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.62rem] uppercase tracking-[0.2em] text-sage">Contenuti</p>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl text-wood">Testi del sito</h1>
          <p className="mt-3 max-w-2xl text-sm text-foreground/65">
            Usa una riga vuota tra due paragrafi per separarli sul sito.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-sage" data-testid="content-saved">
              <Check size={15} /> Salvato
            </span>
          )}
          <AdminButton onClick={save} disabled={saving} data-testid="content-save">
            {saving && <Loader2 size={13} className="animate-spin" />} Salva modifiche
          </AdminButton>
        </div>
      </div>

      {error && <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive" data-testid="content-error">{error}</p>}

      {GROUPS.map((group) => (
        <AdminCard key={group.title} title={group.title} testId={`content-group-${group.title.slice(0, 8)}`}>
          <div className="grid gap-4 md:grid-cols-2">
            {group.fields.map(([key, label, type]) => (
              <div key={key} className={type === "textarea" || type === "list" ? "md:col-span-2" : ""}>
                {type === "boolean" ? (
                  <label className="flex items-center gap-3 text-sm text-foreground/75">
                    <input
                      type="checkbox"
                      checked={!!values[key]}
                      onChange={(e) => setValues({ ...values, [key]: e.target.checked })}
                      className="h-4 w-4 accent-[hsl(var(--sage))]"
                      data-testid={`setting-${key}`}
                    />
                    {label}
                  </label>
                ) : (
                  <AdminField label={label}>
                    {type === "list" ? (
                      <textarea
                        rows={6}
                        value={(Array.isArray(values[key]) ? values[key] : []).join("\n")}
                        onChange={(e) =>
                          setValues({
                            ...values,
                            [key]: e.target.value.split("\n").map((v) => v.trim()).filter(Boolean),
                          })
                        }
                        className={adminInput}
                        data-testid={`setting-${key}`}
                      />
                    ) : type === "textarea" ? (
                      <textarea
                        rows={key.endsWith("_text") ? 6 : 3}
                        value={values[key] ?? ""}
                        onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                        className={adminInput}
                        data-testid={`setting-${key}`}
                      />
                    ) : (
                      <input
                        type={type === "number" ? "number" : "text"}
                        value={values[key] ?? ""}
                        onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                        className={adminInput}
                        data-testid={`setting-${key}`}
                      />
                    )}
                  </AdminField>
                )}
              </div>
            ))}
          </div>
        </AdminCard>
      ))}
    </div>
  );
}
